import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { mapResponse } from './helpers';
import { ApiCreateWebhookRequest, ApiCustomerWebhook, ApiError } from '../openapi';
import { createIdempotencyKey } from '../hooks/useIdempotencyKey';

export function useWebhooksQuery() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['webhooks'],
    queryFn: () => client.GET('/business/current/webhooks').then(mapResponse),
  });
}

export function useWebhookQuery({ webhookId }: { webhookId: string }) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['webhooks', webhookId],
    queryFn: () =>
      client
        .GET('/business/current/webhooks')
        .then(mapResponse)
        .then(({ webhooks }) => {
          const webhook = webhooks.find((it) => it.id === webhookId);
          if (!webhook) {
            throw new ApiError('Webhook not found', 404, 'NotFound');
          }

          return webhook;
        }),
  });
}

export function useCreateWebhookMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['webhooks', 'create'],
    mutationFn: (data: ApiCreateWebhookRequest) =>
      client
        .POST('/business/current/webhooks', {
          body: data,
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(mapResponse),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['webhooks'] }),
  });
}

export function useUpdateWebhookMutation({ webhookId }: { webhookId: string }) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['webhooks', 'update', webhookId],
    mutationFn: (data: ApiCreateWebhookRequest) => {
      const webhook = queryClient.getQueryData<ApiCustomerWebhook>(['webhooks', webhookId]);
      const requests = [];
      if (data.uri && webhook?.uri !== data.uri) {
        requests.push(
          client
            .PUT('/business/current/webhooks/{id}/uri', {
              params: { path: { id: webhookId } as never },
              body: { uri: data.uri },
              headers: { 'Idempotency-Key': createIdempotencyKey() },
            })
            .then(mapResponse),
        );
      }
      if (data.secret && webhook?.secret !== data.secret) {
        requests.push(
          client
            .PUT('/business/current/webhooks/{id}/secret', {
              params: { path: { id: webhookId } as never },
              body: { secret: data.secret },
              headers: { 'Idempotency-Key': createIdempotencyKey() },
            })
            .then(mapResponse),
        );
      }

      if (data.events) {
        requests.push(
          client
            .PUT('/business/current/webhooks/{id}/events', {
              params: { path: { id: webhookId } as never },
              body: { events: data.events },
              headers: { 'Idempotency-Key': createIdempotencyKey() },
            })
            .then(mapResponse),
        );
      }

      return Promise.all(requests);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['webhooks'] }),
  });
}

export function useTerminateWebhookMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['webhooks', 'terminate'],
    mutationFn: ({ id }: { id: string }) =>
      client
        .POST('/business/current/webhooks/{id}/terminate', {
          params: { path: { id } as never },
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(mapResponse),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['webhooks'] }),
  });
}
